/**
 * Whether the backend answered the last request (z8nrz7fh5c).
 *
 * `navigator.onLine` reports the network LINK, not whether the server can be
 * reached: a captive portal or a dead VPN still reads as online. Every API call
 * already passes through `authFetch`, so it reports here what actually
 * happened — any HTTP response means the server is reachable (a 500 is still
 * an answer), and a fetch that threw means it may not be.
 *
 * "May": a browser throws the same error for a request the network dropped
 * and for one it refused by CORS rules, and the two cannot be told apart. So
 * a thrown fetch is confirmed first by a check the app registers
 * (`confirmUnreachableWith`; the mobile app's health probe) before the server
 * is called unreachable. Without one (the desktop app) it counts at once.
 *
 * Plain module state with a subscribe, so `src/lib` stays free of React and the
 * mobile surface reads it through `useSyncExternalStore`. The desktop app
 * reports into it too and simply never reads it.
 */

type Listener = () => void

let reachable = true
const listeners = new Set<Listener>()

/** Record what the last request found. Notifies only on a change. */
export function reportReachable(value: boolean): void {
  if (value === reachable) return
  reachable = value
  for (const listener of listeners) listener()
}

/** What confirms a thrown fetch: resolves false when the server is not there. */
let confirm: (() => Promise<boolean>) | null = null
let confirming: Promise<void> | null = null

/** Register (or with null, remove) the check that confirms a thrown fetch. */
export function confirmUnreachableWith(check: (() => Promise<boolean>) | null): void {
  confirm = check
}

/**
 * A request got no answer at all. Unreachable at once when no check is
 * registered; otherwise only if the check fails too. One check at a time:
 * a burst of failed requests runs it once.
 */
export function reportNoAnswer(): void {
  if (!confirm) return reportReachable(false)
  if (!reachable || confirming) return
  const check = confirm
  confirming = check()
    .catch(() => false)
    .then((there) => {
      if (!there) reportReachable(false)
    })
    .finally(() => {
      confirming = null
    })
}

/** The last request's outcome; optimistic (true) until one has failed. */
export function isServerReachable(): boolean {
  return reachable
}

export function subscribeReachability(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * True when `err` is fetch failing to get any answer at all, not a server that
 * answered with an error. Browsers throw a `TypeError` for that ("Failed to
 * fetch", "Load failed", "NetworkError when attempting to fetch resource").
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
}
